import { IClient } from '@api/models';
import useDialog from '@hooks/use-dialog';
import {
    Avatar,
    Button,
    Card,
    CircularProgress,
    HelpText,
    IconButton,
    Label,
    LinearProgress,
} from '@hyperflake/react-ui-library';
import CardBody from '@hyperflake/react-ui-library/cards/components/CardBody.js';
import CardHeader from '@hyperflake/react-ui-library/cards/components/CardHeader.js';
import useClientData from 'modules/clients/hooks/useClientData';
import { PageTemplate } from 'modules/shared/features/content';
import React, { ReactNode, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import ClientDeleteConfirmationDrawer from '../ClientDeleteConfirmationDrawer/ClientDeleteConfirmationDrawer';
import { ErrorMessage, Form, Formik } from 'formik';
import useClientUpdateBasicForm from 'modules/clients/hooks/useClientUpdateBasicForm';
import { toast } from 'react-toastify';
import useAuthPermission from '@hooks/use-auth-permission';
import { PermissionEnum } from '@api/enum';
import { FormikInput, FormikSelect, FormikSwitch } from 'modules/shared/features/formik';
import ColorPicker from '@components/ColorPicker/ColorPicker';
import { ClientStatusEnum } from '@api/enum';
import upperCase from 'lodash.uppercase';
import { ColorResult } from 'react-color';
import LogoForm from '../LogoForm/LogoForm';
import EmailHeaderForm from '../EmailHeaderForm/EmailHeaderForm';
import ClientLoginImagesForm from '../ClientLoginImagesForm/ClientLoginImagesForm';
import ClientEditFormPlugin from '../ClientEditFormPlugin/ClientEditFormPlugin';
import ClientEditFormBasic from '../ClientEditFormBasic/ClientEditFormBasic';
import { ClientMap } from 'modules/client-map';
import UpArrowIcon from '@icon/ArrowUpIcon';
import DownArrowIcon from '@icon/ArrowDownIcon';
import { SiteWalkMediaViewTypeEnum, SiteWalkMediaViewTypeLabels } from '@api/enum/client.enum';

const ClientDetails = () => {
    // const { unit } = props;
    const { clientId } = useParams();
    const { client, isLoading, setData } = useClientData(clientId);
    const confirmationsDrawerDialog = useDialog<IClient>();
    const updateClientForm = useClientUpdateBasicForm({ client });
    const [openMapPreview, setOpenMapPreview] = useState(true);

    const data: { label: string; value: ReactNode }[] = [
        {
            label: 'Client Id',
            value: client?._id,
        },
        {
            label: 'Name',
            value: (
                <div className="flex items-center gap-2">
                    <Avatar src={client?.logoUrl} alt={client?.name} bgColor={client?.preferences?.primaryColor} />
                    {client?.name}
                </div>
            ),
        },

        {
            label: 'Storage',
            value: client?.storageName,
        },
        {
            label: 'Status',
            value: client?.status,
        },
    ];

    const handleSubmit = async (values: any) => {
        try {
            const updatedClient = await updateClientForm.submit(values);

            handleClientUpdate(updatedClient);

            toast.success('Client updated successfully.');
        } catch (ex: any) {
            toast.error(ex?.data?.message || 'Some error occured, please try again.');
            console.log(ex);
        }
    };
    const { hasPermission } = useAuthPermission();
    const clientStatusOptions = useMemo(() => {
        return Object.values(ClientStatusEnum).map((status) => ({
            value: status,
            label: upperCase(status),
        }));
    }, []);
    const handleClientUpdate = (updatedClient: IClient) => {
        // onClientUpdate(updatedClient);
        setData(updatedClient);
    };
    const handleLogoUrlUpdate = (logoUrl: string) => {
        handleClientUpdate({ ...client, logoUrl });
    };

    const handleEmailHeaderLogoUrlUpdate = (emailHeaderLogoUrl: string) => {
        handleClientUpdate({ ...client, emailHeaderLogoUrl });
    };

    const typeOptions = useMemo(() => {
        return [
            // { label: 'Select Type', value: '' },
            ...Object.values(SiteWalkMediaViewTypeEnum).map((val) => ({
                label: SiteWalkMediaViewTypeLabels[val], // Display human-readable label
                value: val,
            })),
        ];
    }, []);
    return (
        <PageTemplate>
            {/* {isLoading ? (
                <div className="flex flex-col justify-center items-center gap-2  h-36">
                    <CircularProgress />
                    <div className="text-secondary font-medium text-base">Fetching Data... </div>
                </div>
            ) : (
                <>
                    <Card className="">
                        <CardHeader>Client Details</CardHeader>
                        <CardBody>
                            <div className="grid grid-cols-12 gap-6">
                                {data.map((row, index) => (
                                    <div key={index} className="col-span-6 items-start gap-x-8">
                                        <div className="text-sm text-grayscale-500">{row.label} </div>
                                        <h6 className="mt-[6px]">{row.value}</h6>
                                    </div>
                                ))}
                            </div>
                        </CardBody>
                    </Card>
                    <Card className="mt-4">
                        <CardHeader>
                            <h6>Client Delete</h6>
                        </CardHeader>
                        <CardBody>
                            <Button onClick={() => confirmationsDrawerDialog.show(client)}>Delete</Button>
                        </CardBody>
                    </Card>
                </>
            )} */}
            {isLoading ? (
                <LinearProgress />
            ) : (
                // <div className="flex flex-col justify-center items-center gap-2  h-36">
                //     <CircularProgress />
                //     <div className="text-secondary font-medium text-base">Fetching Data... </div>
                // </div>
                <div>
                    <Card className="">
                        <Card.Header>Profile Pictures</Card.Header>
                        <Card.Body>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-start">
                                <div className="md:col-span-1">
                                    <LogoForm client={client} onLogoUrlUpdate={handleLogoUrlUpdate} />
                                </div>

                                <div className="md:col-span-1">
                                    <EmailHeaderForm
                                        client={client}
                                        onEmailHeaderLogoUrlUpdate={handleEmailHeaderLogoUrlUpdate}
                                    />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* TODO modify ClientEditFormBasic */}

                    {/* <ClientEditFormBasic client={client} onClientUpdate={handleClientUpdate} /> */}

                    <Formik
                        initialValues={updateClientForm.initialValues}
                        enableReinitialize
                        validationSchema={updateClientForm.schema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue }) => (
                            <Form>
                                <fieldset disabled={!hasPermission(PermissionEnum.CLIENT_UPDATE)}>
                                    <Card className="mt-4">
                                        <Card.Header>Basic Details</Card.Header>
                                        <Card.Body className="space-y-4">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                                {/* Name */}
                                                <div>
                                                    <Label>Name</Label>
                                                    <FormikInput type="text" name="name" />
                                                </div>

                                                {/* Domain */}
                                                <div>
                                                    <Label>Domain</Label>
                                                    <FormikInput type="text" name="domain" />
                                                </div>

                                                {/* Status */}
                                                <div>
                                                    <Label>Status</Label>
                                                    <FormikSelect name="status" options={clientStatusOptions} />
                                                </div>

                                                {/* Storage */}
                                                <div>
                                                    <Label>Storage</Label>
                                                    <FormikInput type="text" name="storageName" disabled />
                                                </div>
                                            </div>

                                            <div className="pt-2 flex justify-end">
                                                <Button
                                                    type="submit"
                                                    size="lg"
                                                    className="w-full sm:w-auto"
                                                    disabled={updateClientForm.isSubmitting}
                                                >
                                                    {updateClientForm.isSubmitting ? (
                                                        <CircularProgress color="inherit" size={18} />
                                                    ) : (
                                                        'Save'
                                                    )}
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    {/* preferences */}
                                    <Card className="mt-4">
                                        <Card.Header>Preferences</Card.Header>
                                        <Card.Body className="space-y-4">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                                {/* Max Image Viewable Days */}
                                                <div>
                                                    <Label>Max ImageViewable Days</Label>
                                                    <FormikInput type="number" name="preset.maxImageViewableDays" />
                                                </div>

                                                {/* Primary Color */}
                                                <div>
                                                    <Label>Primary Color</Label>
                                                    <ColorPicker
                                                        value={values.preferences.primaryColor}
                                                        setColor={(event: ColorResult) => {
                                                            setFieldValue(
                                                                'preferences.primaryColor',
                                                                event.hex.toUpperCase()
                                                            );
                                                        }}
                                                    />
                                                </div>

                                                <div className="col-span-1 md:col-span-2">
                                                    <Label>Default View</Label>
                                                    <FormikSelect
                                                        name="preferences.siteWalkViewType"
                                                        options={typeOptions}
                                                    />
                                                    <HelpText variant="error">
                                                        <ErrorMessage name="preferences.siteWalkViewType" />
                                                    </HelpText>
                                                </div>

                                                {/* Enable CDN Delivery */}
                                                <div className="flex items-center gap-3">
                                                    <Label
                                                        htmlFor="preferences.hasCdnEnabled"
                                                        className="font-medium text-grayscale-900 mb-0"
                                                    >
                                                        Enable CDN Delivery
                                                    </Label>
                                                    <FormikSwitch color="success" name="preferences.hasCdnEnabled" />
                                                </div>

                                                {/* Social Sharing */}
                                                <div className="flex items-center gap-3">
                                                    <Label
                                                        htmlFor="preferences.allowSocialSharing"
                                                        className="font-medium text-grayscale-900 mb-0"
                                                    >
                                                        Social Sharing
                                                    </Label>
                                                    <FormikSwitch
                                                        color="success"
                                                        name="preferences.allowSocialSharing"
                                                    />
                                                </div>
                                            </div>

                                            <div className="pt-2 flex justify-end">
                                                <Button
                                                    type="submit"
                                                    size="lg"
                                                    className="w-full sm:w-auto"
                                                    disabled={updateClientForm.isSubmitting}
                                                >
                                                    {updateClientForm.isSubmitting ? (
                                                        <CircularProgress color="inherit" size={18} />
                                                    ) : (
                                                        'Save'
                                                    )}
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Card className="mt-4">
                                        <Card.Header>Maps</Card.Header>
                                        <Card.Body className="space-y-4">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                                {/* Latitude */}
                                                <div>
                                                    <Label>Latitude</Label>
                                                    <FormikInput type="number" name="preferences.mapCenter.latitude" />
                                                </div>

                                                {/* Longitude */}
                                                <div>
                                                    <Label>Longitude</Label>
                                                    <FormikInput type="number" name="preferences.mapCenter.longitude" />
                                                </div>

                                                {/* Map Zoom */}
                                                <div>
                                                    <Label>Map Zoom</Label>
                                                    <FormikInput type="number" name="preferences.mapZoom" />
                                                </div>
                                            </div>

                                            <div className="pt-2 flex justify-end">
                                                <Button
                                                    type="submit"
                                                    size="lg"
                                                    className="w-full sm:w-auto"
                                                    disabled={updateClientForm.isSubmitting}
                                                >
                                                    {updateClientForm.isSubmitting ? (
                                                        <CircularProgress color="inherit" size={18} />
                                                    ) : (
                                                        'Save'
                                                    )}
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    {/* <Button
                                        type="submit"
                                        size={'lg'}
                                        className="mt-4"
                                        disabled={updateClientForm.isSubmitting}
                                    >
                                        {updateClientForm.isSubmitting ? (
                                            <CircularProgress color={'inherit'} size={18} />
                                        ) : (
                                            'Save'
                                        )}
                                    </Button> */}
                                </fieldset>
                            </Form>
                        )}
                    </Formik>

                    <Card className="mt-4">
                        <Card.Header>Login Images</Card.Header>
                        <Card.Body>
                            <ClientLoginImagesForm client={client} onClientUpdate={handleClientUpdate} />
                        </Card.Body>
                    </Card>
                    <Card className="mt-4">
                        <Card.Header>Plugins</Card.Header>
                        <Card.Body>
                            <ClientEditFormPlugin onClientUpdate={handleClientUpdate} client={client} />{' '}
                        </Card.Body>
                    </Card>
                    {/* <Card className="mt-4">
                        <Card.Header
                            className="flex items-center justify-between"
                            onClick={() => setOpenMapPreview((v) => !v)}
                        >
                            <span>Map Preview</span>

                            <IconButton size="xs" variant="text">
                                {openMapPreview ? (
                                    <UpArrowIcon width={16} height={16} />
                                ) : (
                                    <DownArrowIcon width={16} height={16} />
                                )}
                            </IconButton>
                        </Card.Header>

                        {openMapPreview && (
                            <Card.Body>
                                <ClientMap />
                            </Card.Body>
                        )}
                    </Card> */}

                    <Card className="mt-4">
                        <Card.Header
                            className="flex items-center justify-between"
                            onClick={() => setOpenMapPreview((v) => !v)}
                        >
                            <span>Map Preview</span>

                            <IconButton size="xs" variant="text">
                                {openMapPreview ? (
                                    <UpArrowIcon width={16} height={16} />
                                ) : (
                                    <DownArrowIcon width={16} height={16} />
                                )}
                            </IconButton>
                        </Card.Header>

                        {openMapPreview && client && (
                            <Card.Body>
                                <ClientMap
                                    client={client}
                                    isLoading={isLoading}
                                    setData={(updatedClient) => setData(updatedClient)}
                                />
                            </Card.Body>
                        )}
                    </Card>

                    <Card className="mt-4">
                        <Card.Header>Client Delete</Card.Header>
                        <Card.Body>
                            <Button onClick={() => confirmationsDrawerDialog.show(client)}>Delete</Button>
                        </Card.Body>
                    </Card>

                    <ClientDeleteConfirmationDrawer
                        open={confirmationsDrawerDialog.isOpen}
                        onClose={confirmationsDrawerDialog.hide}
                        client={confirmationsDrawerDialog.data}
                    />
                </div>
            )}
        </PageTemplate>
    );
};

export default ClientDetails;
